import { useDimensions } from '@shared/utils/useDimensions';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { useAside } from './aside/hooks';
import type { AsideApi } from './aside/types';
import { LayoutBreakpoints, LayoutMode, LayoutSize } from './types';
import { getSize } from './utils';

interface LayoutContextData {
  layout: LayoutMode;
  size: LayoutSize;
  aside: AsideApi;
  domain?: string;
}

const LayoutContext = createContext<LayoutContextData>({
  layout: 'multi-panel',
  size: 'xlarge',
  aside: {
    open() {},
    close() {},
    selected: {
      kind: undefined,
      id: undefined,
    },
    isOpen: false,
  },
});

export const useLayout = () => useContext(LayoutContext);

const layouts: Record<LayoutSize, LayoutMode> = {
  medium: 'stack',
  large: 'multi-panel',
  xlarge: 'multi-panel',
};

interface LayoutProviderProps {
  breakpoints: LayoutBreakpoints;
  domain?: string;
  children: ReactNode;
}

export const LayoutProvider = ({
  children,
  domain,
  breakpoints,
}: LayoutProviderProps) => {
  const { dimensions, ref } = useDimensions();
  const aside = useAside();
  const size = getSize(breakpoints, dimensions.width);
  const layout = layouts[size];

  return (
    <LayoutContext.Provider value={{ layout, size, aside, domain }}>
      <div ref={ref}>{children}</div>
    </LayoutContext.Provider>
  );
};
